import * as Ably from "ably";
import { useEffect, useRef, useState } from "react";
import {
  fetchAblyBoardToken,
  managementTenantKey,
} from "@/lib/management-api";

export const ABLY_EVENT_CREATED = "request.created";
export const ABLY_EVENT_ACKNOWLEDGED = "request.acknowledged";
export const ABLY_EVENT_ESCALATED = "request.escalated";
export const ABLY_EVENT_ESCALATION_EXHAUSTED = "request.escalation_exhausted";

export type AblyLiveStatus = "online" | "offline";

export type ConciergeBoardRealtimePayload = {
  event?: string;
  Event?: string;
  tenantKey?: string;
  TenantKey?: string;
  patientRequestId?: number;
  PatientRequestId?: number;
  status?: string;
  Status?: string;
  requestCode?: string;
  RequestCode?: string;
  requestTitle?: string | null;
  RequestTitle?: string | null;
  patientTranscript?: string | null;
  PatientTranscript?: string | null;
  currentTierOrder?: number;
  CurrentTierOrder?: number;
  escalationRoleCode?: string | null;
  EscalationRoleCode?: string | null;
  escalationTierName?: string | null;
  EscalationTierName?: string | null;
  createdDateTimeUtc?: string;
  CreatedDateTimeUtc?: string;
  updatedDateTimeUtc?: string;
  UpdatedDateTimeUtc?: string;
  deviceId?: string;
  DeviceId?: string;
  deviceName?: string | null;
  DeviceName?: string | null;
  organizationName?: string | null;
  OrganizationName?: string | null;
  siteName?: string | null;
  SiteName?: string | null;
  locationName?: string | null;
  LocationName?: string | null;
  room?: string | null;
  Room?: string | null;
  bed?: string | null;
  Bed?: string | null;
  acceptedByUserId?: string | null;
  AcceptedByUserId?: string | null;
};

/** Last-resort only — prefer ChannelName from GET .../ably/token. */
export function resolveAblyChannelName(tenantKey: string): string {
  const key = tenantKey.trim().toUpperCase();
  return `patient-concierge:${key}:board`;
}

export function parseAblyPayload(
  data: unknown,
): ConciergeBoardRealtimePayload | null {
  if (data == null) return null;
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as ConciergeBoardRealtimePayload;
    } catch {
      return null;
    }
  }
  if (typeof data === "object") {
    return data as ConciergeBoardRealtimePayload;
  }
  return null;
}

function mapConnectionState(state: string): AblyLiveStatus {
  return state === "connected" ? "online" : "offline";
}

export function useConciergeBoardAbly(options: {
  tenantKey: string;
  enabled?: boolean;
  onCreated: (payload: ConciergeBoardRealtimePayload) => void;
  onAcknowledged: (payload: ConciergeBoardRealtimePayload) => void;
  onEscalated?: (payload: ConciergeBoardRealtimePayload) => void;
}): AblyLiveStatus {
  const {
    tenantKey,
    enabled = true,
    onCreated,
    onAcknowledged,
    onEscalated,
  } = options;
  const [status, setStatus] = useState<AblyLiveStatus>("offline");
  const createdRef = useRef(onCreated);
  const ackRef = useRef(onAcknowledged);
  const escalatedRef = useRef(onEscalated);
  createdRef.current = onCreated;
  ackRef.current = onAcknowledged;
  escalatedRef.current = onEscalated;

  useEffect(() => {
    if (!enabled) {
      setStatus("offline");
      return;
    }

    let cancelled = false;
    let client: Ably.Realtime | null = null;
    setStatus("offline");

    const key = (tenantKey || managementTenantKey()).trim();

    void (async () => {
      // Probe once so we skip spinning up Ably when tokens aren't available.
      const probe = await fetchAblyBoardToken(key);
      if (cancelled) return;

      if (!probe.ok || !probe.tokenRequest) {
        setStatus("offline");
        console.info(
          "[ably]",
          probe.message || "board token unavailable — poll only",
        );
        return;
      }

      const channelName =
        probe.channelName || resolveAblyChannelName(key);

      let seedToken: unknown = probe.tokenRequest;

      client = new Ably.Realtime({
        authCallback: (_tokenParams, callback) => {
          if (seedToken) {
            const token = seedToken;
            seedToken = null;
            callback(null, token as Ably.TokenRequest);
            return;
          }
          void fetchAblyBoardToken(key)
            .then((result) => {
              if (!result.ok || !result.tokenRequest) {
                callback(
                  result.message || "Unable to obtain Ably board token",
                  null,
                );
                return;
              }
              callback(null, result.tokenRequest as Ably.TokenRequest);
            })
            .catch((err: unknown) => {
              const message =
                err instanceof Error
                  ? err.message
                  : "Ably token request failed";
              callback(message, null);
            });
        },
      });

      const onState = () => {
        if (cancelled || !client) return;
        setStatus(mapConnectionState(client.connection.state));
      };

      client.connection.on(onState);
      onState();

      const channel = client.channels.get(channelName);
      const handle = (message: Ably.Message) => {
        const payload = parseAblyPayload(message.data);
        if (!payload) return;
        const name = message.name || payload.event || payload.Event || "";
        if (name === ABLY_EVENT_CREATED) {
          createdRef.current(payload);
        } else if (name === ABLY_EVENT_ACKNOWLEDGED) {
          ackRef.current(payload);
        } else if (
          name === ABLY_EVENT_ESCALATED ||
          name === ABLY_EVENT_ESCALATION_EXHAUSTED
        ) {
          escalatedRef.current?.(payload);
        }
      };

      try {
        await channel.subscribe(handle);
        if (!cancelled) {
          console.info("[ably] subscribed", channelName);
        }
      } catch (err) {
        console.warn("[ably] subscribe failed", err);
        if (!cancelled) setStatus("offline");
      }
    })();

    return () => {
      cancelled = true;
      if (client) {
        client.close();
      }
    };
  }, [tenantKey, enabled]);

  return status;
}
